import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { RootState, useDispatch, useSelector } from "src/store";
import { selectDeviceById } from "src/ui/containers/dashboard/devices/store/selectors/devices";
import { MdmCommand } from "src/ui/containers/dashboard/devices/types/commands";
import { Tooltip } from "src/ui/shared/Tooltip";
import { makeStyles } from "tss-react/mui";

import { ConfirmDialog } from "@dashboard/devices/components/shared/ConfirmDialog";
import { createDeviceMdmCommand } from "@devices/api/devices";
import { fetchDevice } from "@devices/store";
import { getActiveConnectionType, getWifiMac } from "@devices/utils/device";
import { Box, DialogContentText, Typography } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";
import { UncontrolledSwitch } from "@shared/form/SwitchField";
import { ActivityIcon } from "@shared/Loading";
import { MinimalTable, MinimalTableContainer, MinimalTableHeader, MinimalTableRow } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { TableHeaderRows } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { TableRowCells } from "@shared/table/TableRowCells";
import { showErrorToast } from "@shared/toasts/Toasts";
import { PaneElement } from "@sidePanel/shared/PaneElement";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { AddWifiNetworkModal } from "./AddWifiNetworkModal";
import {
    columns,
    isWifiEnabled,
    TableRowData,
    useGetTableData,
    useLastAddedWifiCommand,
    useLastChangeWifiCommand,
    useWifiCommands,
} from "./WifiSection.utils";

const useStyles = makeStyles()((theme) => ({
    switchRow: {
        flex: 1,
        display: "flex",
        justifyContent: "flex-start",
    },
    wifiSwitch: {
        marginLeft: theme.spacing(-1.5),
    },
    tableSection: {
        marginTop: theme.spacing(2),
    },
    headerBox: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        paddingBottom: theme.spacing(2),
    },
    sectionLabel: {
        fontSize: 14,
        fontWeight: 600,
    },
}));

type Props = { deviceId: number };

const WifiSection = (props: Props) => {
    const [confirmDisableOpen, setConfirmDisableOpen] = React.useState(false);
    const [addNetworkOpen, setAddNetworkOpen] = React.useState(false);
    const [isRefetchingLoading, setIsRefetchingLoading] = React.useState(false);

    const { classes } = useStyles();
    const { t } = useTranslation(["common", "sidepanels", "modals"]);
    const dispatch = useDispatch();

    const refetchDevice = async () => {
        try {
            setIsRefetchingLoading(true);
            await dispatch(fetchDevice({ deviceId: props.deviceId })).unwrap();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:devicePanel.toast.refetchFailed"));
        } finally {
            setIsRefetchingLoading(false);
        }
    };

    const onEnableWifiSettled = (command?: MdmCommand) => {
        if (command && command.status !== "successful") {
            showErrorToast(t("sidepanels:devicePanel.toast.enableWifiFailed"));
        }
        void refetchDevice();
    };

    const { commands: wifiCommands, refresh: refreshWifiCommands } = useWifiCommands(props.deviceId);
    const lastChangeWifiCommandContext = useLastChangeWifiCommand(wifiCommands, onEnableWifiSettled);
    const lastAddedCommandContext = useLastAddedWifiCommand(wifiCommands, refetchDevice);

    const device = useSelector((state: RootState) => selectDeviceById(state, props.deviceId));

    const connectionType = getActiveConnectionType(device?.lastState?.networks, !!device?.isOnline);
    const wifiEnabled = isWifiEnabled(device);
    const wifiMac = getWifiMac(device?.lastState) ?? "-";
    const data = useGetTableData(device?.lastState?.wifi, lastAddedCommandContext.lastAddedCommand);

    const sendEnableWifiCommand = async (nextEnabled: boolean) => {
        try {
            await createDeviceMdmCommand({
                deviceId: props.deviceId,
                json: { command: "enableWifi", enable: nextEnabled },
            });

            await refreshWifiCommands();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:devicePanel.toast.commandSentFailed"));
        }
    };

    const handleToggle = (_event: React.ChangeEvent<HTMLInputElement>, nextEnabled: boolean) => {
        if (!nextEnabled && connectionType === "wifi") {
            setConfirmDisableOpen(true);
            return;
        }
        void sendEnableWifiCommand(nextEnabled);
    };

    const savedNetworksTable = useReactTable<TableRowData>({
        data,
        columns,
        initialState: { pagination: { pageSize: 12, pageIndex: 0 } },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const { pageSize, pageIndex } = savedNetworksTable.getState().pagination;

    const isSwitchPending = lastChangeWifiCommandContext.isPending || isRefetchingLoading;
    const isAddNetworkPending = lastAddedCommandContext.isPending || isRefetchingLoading;

    const getTooltipTitle = () => {
        if (!device?.isOnline) {
            return t("sidepanels:devicePanel.tooltip.deviceOffline");
        }
        if (isSwitchPending) {
            return t("sidepanels:devicePanel.tooltip.commandPending");
        }
        return "";
    };

    const getAddNetworkButtonTooltipTitle = () => {
        if (!device?.isOnline) {
            return t("sidepanels:devicePanel.tooltip.deviceOffline");
        }
        if (isAddNetworkPending) {
            return t("sidepanels:devicePanel.tooltip.commandPending");
        }
        return t("sidepanels:devicePanel.tooltip.addNetwork");
    };

    return (
        <PaneElement.Section>
            <PaneElement.Header>{t("sidepanels:devicePanel.networkTab.wifi")}</PaneElement.Header>

            <PropertyRow>
                <PropertyKey>{t("sidepanels:devicePanel.networkTab.status")}</PropertyKey>
                <Box className={classes.switchRow}>
                    <Tooltip title={getTooltipTitle()}>
                        {isSwitchPending ? (
                            <ActivityIcon />
                        ) : (
                            <UncontrolledSwitch
                                value={wifiEnabled}
                                disabled={!device?.isOnline || isSwitchPending}
                                onChange={handleToggle}
                                className={classes.wifiSwitch}
                            />
                        )}
                    </Tooltip>
                </Box>
            </PropertyRow>

            <PropertyRow>
                <PropertyKey>{t("sidepanels:devicePanel.networkTab.mac")}</PropertyKey>
                <PropertyValue>{wifiMac}</PropertyValue>
            </PropertyRow>

            <Box className={classes.tableSection}>
                <Box className={classes.headerBox}>
                    <Typography className={classes.sectionLabel}>
                        {t("sidepanels:devicePanel.networkTab.knownNetworks")}
                    </Typography>
                    <SecondaryButton
                        onClick={() => setAddNetworkOpen(true)}
                        tooltipProps={{ title: getAddNetworkButtonTooltipTitle() }}
                        disabled={!device?.isOnline || isAddNetworkPending}
                    >
                        <i className="fa-solid fa-plus" />
                    </SecondaryButton>
                </Box>
                <MinimalTableContainer>
                    <MinimalTable>
                        <MinimalTableHeader>
                            <TableHeaderRows headerGroups={savedNetworksTable.getHeaderGroups()} />
                        </MinimalTableHeader>
                        <TableBody table={savedNetworksTable} noDataMessage={t("sidepanels:devicePanel.table.noSavedNetworks")}>
                            {savedNetworksTable.getRowModel().rows.map((row) => (
                                <MinimalTableRow key={row.original.id}>
                                    <TableRowCells row={row} />
                                </MinimalTableRow>
                            ))}
                        </TableBody>
                    </MinimalTable>
                </MinimalTableContainer>
                <CustomTablePagination
                    component="div"
                    rowsPerPageOptions={[12, 24, 48]}
                    count={savedNetworksTable.getFilteredRowModel().rows.length}
                    rowsPerPage={pageSize}
                    page={pageIndex}
                    onPageChange={(_event, page) => savedNetworksTable.setPageIndex(page)}
                    onRowsPerPageChange={(e) => {
                        const size = e.target.value ? Number(e.target.value) : 12;
                        savedNetworksTable.setPageSize(size);
                    }}
                />
            </Box>

            <ConfirmDialog
                title={t("modals:wifiDisable.caption")}
                open={confirmDisableOpen}
                onConfirm={() => void sendEnableWifiCommand(false)}
                onClose={() => setConfirmDisableOpen(false)}
                dangerButton
                primaryActionText={t("common:button.disable")}
            >
                <DialogContentText>
                    <Trans ns="modals" i18nKey="wifiDisable.description" components={{ bold: <strong /> }} />
                </DialogContentText>
            </ConfirmDialog>

            <AddWifiNetworkModal
                deviceId={props.deviceId}
                open={addNetworkOpen}
                onClose={() => setAddNetworkOpen(false)}
                onCreated={refreshWifiCommands}
            />
        </PaneElement.Section>
    );
};

export default WifiSection;
